import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { CalendarRange } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { PeriodFilterProps } from '@/components/date-range-filter';
import { PeriodSheet } from '@/components/period/period-sheet';
import { mobileLayer } from '@/lib/mobile-layer';
import {
  applyCustomRange,
  applyPreset,
  getCustomChipLabel,
  getSelectedChip,
  getVisibleChips,
  type PeriodChipPreset,
} from '@/lib/period-chips';
import { cn } from '@/lib/utils';

const chipClass = (pressed: boolean) =>
  cn(
    'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    mobileLayer.chip,
    'min-h-touch',
    pressed
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-input bg-background text-foreground hover:bg-accent'
  );

/**
 * Mobile period selector (SHELL-07 AC 1–4): chips for the presets the page
 * offers plus "Personalizado", which opens the single-month sheet. Drives the
 * same setters as `DateRangeFilter`, so URL and local state work unchanged.
 */
export function PeriodChips({
  fromDate,
  toDate,
  predefinedDateRange,
  predefinedDateRanges,
  setPredefinedDateRange,
  setFromDate,
  setToDate,
}: PeriodFilterProps) {
  const { LL, locale } = useI18nContext();
  const [sheetOpen, setSheetOpen] = useState(false);
  const customRef = useRef<HTMLButtonElement>(null);

  const setters = { setPredefinedDateRange, setFromDate, setToDate };
  const selected = getSelectedChip(predefinedDateRange);
  const chipLabel: Record<PeriodChipPreset, string> = {
    today: LL.shell.period.chips.today(),
    last_7_days: LL.shell.period.chips.last7Days(),
    last_30_days: LL.shell.period.chips.last30Days(),
    last_3_months: LL.shell.period.chips.last3Months(),
  };
  const customLabel =
    getCustomChipLabel({
      predefinedDateRange,
      ranges: predefinedDateRanges,
      fromDate,
      toDate,
      locale,
    }) ?? LL.shell.period.chips.custom();

  // The sheet starts from the current custom dates, never from a preset.
  const isCustomDates =
    predefinedDateRange === undefined && !!fromDate && !!toDate;
  const initialFrom =
    isCustomDates && fromDate
      ? DateTime.fromISO(fromDate).toJSDate()
      : undefined;
  const initialTo =
    isCustomDates && toDate ? DateTime.fromISO(toDate).toJSDate() : undefined;

  return (
    <>
      <div
        role="group"
        className="-mx-1 flex min-w-0 max-w-full gap-2 overflow-x-auto px-1 py-0.5 [scrollbar-width:none]"
      >
        {getVisibleChips(predefinedDateRanges).map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={selected === preset}
            className={chipClass(selected === preset)}
            onClick={() => applyPreset(setters, predefinedDateRanges, preset)}
          >
            {chipLabel[preset]}
          </button>
        ))}
        <button
          ref={customRef}
          type="button"
          aria-pressed={selected === 'custom'}
          className={chipClass(selected === 'custom')}
          onClick={() => setSheetOpen(true)}
        >
          <CalendarRange className="size-4" aria-hidden />
          {customLabel}
        </button>
      </div>
      <PeriodSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        initialFrom={initialFrom}
        initialTo={initialTo}
        returnFocusRef={customRef}
        onApply={(from, to) => applyCustomRange(setters, from, to)}
      />
    </>
  );
}
